import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

interface Component {
  sku: string;
  quantity: number;
  name?: string;
  price?: number;
}

interface Product {
  sku: string;
  name: string;
  description: string;
  category_id: number;
  price: number;
  is_combo: boolean;
  tax_rate: number;
  ean12: string;
  peso: number;
  longitud: number;
  ancho: number;
  alto: number;
  instrucciones_empaque: string;
  imagen: string;
  components: Component[];
}

const ProductView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axiosInstance.get(`/products/${id}?SinSaldo=true`);
        const productData = response.data;
        // Asegurar que el precio sea un número
        if (typeof productData.price === 'string') {
          productData.price = parseFloat(productData.price);
        }
        setProduct(productData);
      } catch (err) {
        console.error('Error obteniendo producto:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return <div className="text-center text-custom-gray-dark">Cargando producto...</div>;
  }

  if (!product) {
    return <div className="text-center text-red-500">Producto no encontrado</div>;
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 bg-white shadow-md rounded-lg">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/products')}
          className="text-custom-blue hover:text-custom-blue/80 mr-4"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className=" ">Detalles del Producto</h1>
      </div>

      <div className="space-y-6">
        {/* Información Básica */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h2 className="text-lg font-semibold mb-4 text-custom-blue">Información Básica</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <p className="mt-1 font-semibold">{product.sku}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <p className="mt-1 font-semibold">{product.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <p className="mt-1 font-semibold">{product.description || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Precios e Impuestos */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h2 className="text-lg font-semibold mb-4 text-custom-blue">Precios e Impuestos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio</label>
              <p className="mt-1 font-semibold">${typeof product.price === 'string' ? parseFloat(product.price).toFixed(2) : product.price.toFixed(2)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IVA (%)</label>
              <p className="mt-1 font-semibold">{product.tax_rate * 100}%</p>
            </div>
          </div>
        </div>

        {/* Dimensiones y Peso */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h2 className="text-lg font-semibold mb-4 text-custom-blue">Dimensiones y Peso</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Peso (kg)</label>
              <p className="mt-1 font-semibold">{product.peso || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitud (cm)</label>
              <p className="mt-1 font-semibold">{product.longitud || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ancho (cm)</label>
              <p className="mt-1 font-semibold">{product.ancho || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alto (cm)</label>
              <p className="mt-1 font-semibold">{product.alto || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Información Adicional */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h2 className="text-lg font-semibold mb-4 text-custom-blue">Información Adicional</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">EAN12</label>
              <p className="mt-1 font-semibold">{product.ean12 || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL de la Imagen</label>
              <p className="mt-1 font-semibold">{product.imagen || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instrucciones de Empaque</label>
              <p className="mt-1 font-semibold">{product.instrucciones_empaque || 'N/A'}</p>
            </div>
          </div>
        </div>

        {product.is_combo && (
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h2 className="text-lg font-semibold mb-4 text-custom-blue">Componentes del Combo</h2>
            <div className="space-y-4">
              {product.components.map((component, index) => (
                <div key={index} className="p-4 bg-white border border-gray-200 rounded-md">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-500">SKU</label>
                      <p className="mt-1 font-semibold">{component.sku}</p>
                    </div>
                    <div className="col-span-6">
                      <label className="block text-sm font-medium text-gray-500">Nombre</label>
                      <p className="mt-1 font-semibold">{component.name || 'N/A'}</p>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-500">Precio</label>
                      <p className="mt-1 font-semibold">
                        ${typeof component.price === 'string' 
                          ? parseFloat(component.price).toFixed(2) 
                          : component.price?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductView; 